// apps/web/src/pages/admin/UserApprovalPage.tsx
import React, { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, ChevronLeft, ChevronRight, InfoIcon, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { UserOnboardingStatus } from '@logistics-platform/types'; // Ensure types are available

const UserApprovalPage = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const utils = trpc.useUtils();
  
  // Use getAllUsers with filter for pending_admin_approval
  const { data, isLoading, error } = trpc.admin.getAllUsers.useQuery({
    page,
    pageSize,
    search: debouncedSearchQuery,
    onboardingStatus: 'pending_admin_approval', // Filter for pending users
    orderBy: 'verificationSubmittedAt',
    orderDirection: 'asc',
  });

  const approveMutation = trpc.admin.approveUser.useMutation({
    onSuccess: () => { 
        toast.success("User approved!"); 
        utils.admin.getAllUsers.invalidate(); 
    },
    onError: (err) => toast.error(err.message),
  });

  const users = data?.users || [];
  const totalUsers = data?.totalCount || 0;
  const totalPages = Math.ceil(totalUsers / pageSize);

  if (error) {
      return (
          <div className="container mx-auto py-12 px-4">
              <Alert variant="destructive">
                  <InfoIcon className="h-4 w-4" />
                  <AlertTitle>Error Loading Users</AlertTitle>
                  <AlertDescription>{error.message}</AlertDescription>
              </Alert>
          </div>
      );
  }

  return (
    <div className="container mx-auto py-6 px-2 sm:px-4 max-w-full animate-fade-in">
      <Card className="glass">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-2xl flex items-center gap-2">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-primary"/>
              User Approval Queue
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">Review and approve users waiting for admin verification.</CardDescription>
        </CardHeader>
        <CardContent className="p-2 sm:p-6 space-y-4">
          
          {/* Search */}
          <div className="flex w-full max-w-sm items-center space-x-2">
            <Input 
                placeholder="Search by name or email..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 sm:h-10 text-sm"
            />
          </div>

          <div className="rounded-md border bg-card text-card-foreground shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Name</TableHead>
                  <TableHead className="w-[200px]">Email</TableHead>
                  <TableHead className="w-[100px]">Role</TableHead>
                  <TableHead className="w-[150px]">Submitted At</TableHead>
                  <TableHead className="text-right w-[100px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
                    ))
                ) : users.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center h-24 text-muted-foreground">No users are pending approval.</TableCell></TableRow>
                ) : (
                    users.map(user => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.profile?.fullName || 'N/A'}</TableCell>
                        <TableCell className="text-muted-foreground">{user.email}</TableCell>
                        <TableCell><span className="capitalize badge badge-outline">{user.role.toLowerCase()}</span></TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                            {user.verificationSubmittedAt ? new Date(user.verificationSubmittedAt).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="success" onClick={() => approveMutation.mutate({ userId: user.id })} disabled={approveMutation.isLoading}>
                            {approveMutation.isLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle size={14} className="mr-1"/>}
                            Approve
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center pt-4">
                <p className="text-sm text-muted-foreground">
                    Showing {Math.min(totalUsers, (page - 1) * pageSize + 1)} - {Math.min(totalUsers, page * pageSize)} of {totalUsers} users
                </p>
                <div className="flex gap-2">
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setPage(prev => Math.max(1, prev - 1))} 
                        disabled={page === 1}
                    >
                        <ChevronLeft className="h-4 w-4" /> Previous
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setPage(prev => prev + 1)} 
                        disabled={page >= totalPages}
                    >
                        Next <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

        </CardContent>
      </Card>
    </div>
  );
};
export default UserApprovalPage;